import dayjs from "dayjs"
import { getUserFullName } from "../../../helpers/userDisplay"

/** Static Figma City Detail fixtures still used where APIs are not ready. */

export const CITY_GROWTH_PERIOD_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
]

export const DEFAULT_CITY_GROWTH_PERIOD = "monthly"

/** Map profileGenome spellbook items → City Detail donut / list rows. */
export const mapCitySpellbookSegments = (items = []) =>
  (Array.isArray(items) ? items : []).map(item => ({
    name: item.label || item.name || "--",
    percent: Math.round(Number(item.value ?? item.percent) || 0),
    color: item.color || "#8b5cf6",
  }))

const formatRelativeTime = dateString => {
  if (!dateString) return ""
  const date = dayjs(dateString)
  if (!date.isValid()) return ""

  const diffSec = Math.max(0, dayjs().diff(date, "second"))
  if (diffSec < 60) return "Just now"

  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} min ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`
  }

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`
  }

  return date.format("MMM D, YYYY")
}

const humanizeKey = key => {
  if (!key) return ""
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase())
}

const resolveActivityActor = properties => {
  if (!properties || typeof properties !== "object") return ""
  // Login/logout payloads put user fields on properties root.
  // modelActivity payloads nest entity data under new/old — ignore those.
  if (properties.new || properties.old || properties.changed) return ""
  return getUserFullName(properties) || ""
}

const buildCityActivitySubtitle = (log, properties) => {
  if (properties.email) return properties.email
  if (properties.city) {
    return [properties.city, properties.state, properties.country]
      .filter(Boolean)
      .join(", ")
  }

  const entity =
    (properties.new && typeof properties.new === "object" && properties.new) ||
    (properties.old && typeof properties.old === "object" && properties.old) ||
    {}

  if (entity.title) return entity.title
  if (entity.name) return entity.name
  if (entity.description) return entity.description
  if (entity.status) return `Status: ${humanizeKey(entity.status)}`

  const changed =
    properties.changed && typeof properties.changed === "object"
      ? properties.changed
      : null
  if (changed?.status) return `Status: ${humanizeKey(changed.status)}`

  if (properties.event && log?.subject_type) {
    return `${humanizeKey(properties.event)} ${humanizeKey(log.subject_type)}`
  }

  if (log?.subject_type) return humanizeKey(log.subject_type)
  return "Platform activity"
}

const inferActivityIcon = log => {
  const haystack = [
    log?.log_name,
    log?.description,
    log?.subject_type,
    log?.properties?.event,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

  if (/flag|report|safety/.test(haystack)) return "flag"
  if (/call for|call_for|search-status|artist/.test(haystack)) return "search"
  if (/photo|photography|camera|media|upload/.test(haystack)) return "inbox"
  if (/reward|xp|award|review/.test(haystack)) return "award"
  if (/message|chat|inbox/.test(haystack)) return "inbox"
  if (/city|transfer|map/.test(haystack)) return "map"
  if (/member|join|user|logged ?in|logged ?out|login|logout/.test(haystack)) {
    return "user"
  }
  if (/project|task/.test(haystack)) return "award"
  return "user"
}

const logBelongsToCity = (log, cityId) => {
  if (!cityId) return true
  const target = String(cityId)
  const properties =
    log?.properties && typeof log.properties === "object" ? log.properties : {}
  const entity =
    (properties.new && typeof properties.new === "object" && properties.new) ||
    (properties.old && typeof properties.old === "object" && properties.old) ||
    {}

  const candidates = [
    log?.city_id,
    log?.city?.id,
    properties.city_id,
    properties.city?.id,
    entity.city_id,
    entity.city?.id,
  ]
    .filter(Boolean)
    .map(value => String(value))

  // If the payload has no city metadata, trust the server city_id filter.
  if (candidates.length === 0) return true
  return candidates.includes(target)
}

/** Map `/api/v1/admin/activity-logs?city_id=` → City Detail Recent Activities. */
export const mapCityRecentActivities = (result, limit = 5, cityId = "") => {
  const rows = Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
      ? result.data
      : []

  return rows
    .filter(log => logBelongsToCity(log, cityId))
    .slice(0, limit)
    .map((log, index) => {
      const properties = log?.properties || {}
      const actor = resolveActivityActor(properties)
      const description =
        log?.description || humanizeKey(log?.log_name) || "Activity"

      return {
        id: log.id ?? `city-activity-${index}`,
        title: actor ? `${actor} — ${description}` : description,
        subtitle: buildCityActivitySubtitle(log, properties),
        time: formatRelativeTime(log.created_at),
        icon: inferActivityIcon(log),
      }
    })
}

const formatStatValue = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  return num.toLocaleString("en-US")
}

const formatSignedValue = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  if (num > 0) return `+${num.toLocaleString("en-US")}`
  return num.toLocaleString("en-US")
}

const formatGrowthPercent = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  const formatted = `${Math.abs(num).toLocaleString("en-US", {
    maximumFractionDigits: 1,
  })}%`
  if (num > 0) return `+${formatted}`
  if (num < 0) return `-${formatted}`
  return formatted
}

const GROWTH_BAR_COLORS = [
  "#fff1e6",
  "#ffe2c5",
  "#ffd7b3",
  "#ffb07a",
  "#ff822f",
  "#ff7214",
  "#ff6b09",
]

const KPI_META = [
  {
    key: "stats",
    title: "City Members",
    label: "Total Members",
    tone: "lightblue-custom",
    to: "members",
    module: "members",
    getValue: result => result.total_members,
  },
  {
    key: "projects",
    title: "Active Projects",
    label: "Total Projects",
    tone: "purple-custom",
    to: "projects",
    module: "projects",
    getValue: result => result.active_projects,
  },
  {
    key: "sessions",
    title: "Shadow Working Events",
    label: "Live Now",
    tone: "peach",
    to: "shadow-working-events",
    module: "sessions",
    getValue: result => result.shadow_working_events,
  },
]

const PENDING_META = [
  {
    key: "reviews",
    title: "Reviews",
    subtitle: "Awaiting Review",
    icon: "star",
    resultKey: "reviews",
    module: "reviews",
  },
  {
    key: "calls",
    title: "Calls",
    subtitle: "Awaiting Approval",
    icon: "search",
    resultKey: "calls",
    module: "calls",
  },
  {
    key: "photography",
    title: "Photography",
    subtitle: "Photos Awaiting Approval",
    icon: "camera",
    resultKey: "photography",
    module: "photography",
  },
  {
    key: "flags",
    title: "Flags",
    subtitle: "Flags to Review",
    icon: "flag",
    resultKey: "flags",
    module: "flags",
  },
  {
    key: "transfers",
    title: "Transfers",
    subtitle: "Transfer Requests",
    icon: "transfer",
    resultKey: "transfer_requests",
    module: "city-update-requests",
  },
]

const pickChartLabels = (labels = [], count = 5) => {
  if (!labels.length) return []
  if (labels.length <= count) {
    return labels.map(label =>
      dayjs(label).isValid() ? dayjs(label).format("MMM D") : String(label),
    )
  }
  const step = (labels.length - 1) / (count - 1)
  return Array.from({ length: count }, (_, index) => {
    const raw = labels[Math.round(index * step)]
    return dayjs(raw).isValid() ? dayjs(raw).format("MMM D") : String(raw)
  })
}

const mapBarHeights = (values = [], maxHeight = 199) => {
  if (!values.length) return []
  const max = Math.max(...values.map(Number).filter(Number.isFinite), 1)
  return values.map(value => {
    const num = Number(value) || 0
    if (num <= 0) return 4
    return Math.max(8, Math.round((num / max) * maxHeight))
  })
}

/** Map `/api/v1/admin/dashboard/stats?city_id=` → KPI cards. */
export const mapCityDetailKpi = (result = {}) =>
  KPI_META.map(meta => ({
    key: meta.key,
    title: meta.title,
    label: meta.label,
    tone: meta.tone,
    to: meta.to,
    module: meta.module,
    value: formatStatValue(meta.getValue(result)),
  }))

/** Map pending_items block → pending attention cards. */
export const mapCityPendingItems = (result = {}) => {
  const pending = result.pending_items || {}
  return PENDING_META.map(meta => ({
    key: meta.key,
    title: meta.title,
    subtitle: meta.subtitle,
    icon: meta.icon,
    module: meta.module,
    count: formatStatValue(pending[meta.resultKey]),
  }))
}

export const getCityPendingTotal = (result = {}) => {
  const total = Number(result.pending_items?.total)
  if (Number.isFinite(total)) return total
  const pending = result.pending_items || {}
  return PENDING_META.reduce(
    (sum, meta) => sum + (Number(pending[meta.resultKey]) || 0),
    0,
  )
}

/** Map member_growth block → chart + summary stats. */
export const mapCityMemberGrowth = (result = {}) => {
  const growth = result.member_growth || {}
  const series = Array.isArray(growth.members) ? growth.members : []
  const labels = Array.isArray(growth.labels) ? growth.labels : []
  const heights = mapBarHeights(series)

  return {
    labels: pickChartLabels(labels),
    heights,
    colors: heights.map(
      (_, index) =>
        GROWTH_BAR_COLORS[
          Math.min(
            GROWTH_BAR_COLORS.length - 1,
            Math.floor(
              (index / Math.max(heights.length - 1, 1)) *
                (GROWTH_BAR_COLORS.length - 1),
            ),
          )
        ],
    ),
    stats: [
      {
        value: formatStatValue(growth.total_members ?? result.total_members),
        label: "Total Members",
      },
      {
        value: formatSignedValue(growth.new_this_week),
        label: "New This Week",
      },
      {
        value: formatSignedValue(growth.new_this_month),
        label: "New This Month",
      },
      {
        value: formatGrowthPercent(growth.growth_percentage),
        label: "vs last period",
      },
    ],
  }
}
